import { useCallback, useEffect, useState } from "react";

interface UsePersistPanelSizeProps {
    /** Minimum valid size for the panel (inclusive) */
    minSize?: number;
    /** Maximum valid size for the panel (inclusive) */
    maxSize?: number;
    /** Initial size for the panel */
    initialSize?: number;
}

interface UsePersistPanelSizeReturn {
    /** Current size of the panel */
    size: number | undefined;
    /** Handler function to call when the panel is resized */
    handleResize: (size: number) => void;
}

/**
 * Hook for persisting panel resize events with size validation.
 * Stores the last valid size and provides a handler for resize events.
 *
 * @param props Configuration options for the resize handler
 * @returns Object containing the current size and resize handler
 */
export const usePersistPanelSize = ({
    minSize = 10,
    maxSize = 90,
    initialSize = 50,
}: UsePersistPanelSizeProps = {}): UsePersistPanelSizeReturn => {
    const [size, setSize] = useState<number | undefined>(initialSize);

    useEffect(() => {
        setSize(initialSize);
    }, [initialSize]);

    const handleResize = useCallback(
        (newSize: number) => {
            if (newSize >= minSize && newSize <= maxSize) {
                setSize(newSize);
            }
        },
        [minSize, maxSize],
    );

    return {
        size,
        handleResize,
    };
};
